import { abilityFor, DEFAULT_ABILITY, DIFFICULTY_ORDER, gradeToScore, updateAbility } from "./difficulty";
import type {
  CardProgress,
  Difficulty,
  Grade,
  ImportJob,
  LearningExercise,
  LearningPathId,
  LearningState,
  ModeId,
  SessionEvent,
} from "../types";

const dayMs = 24 * 60 * 60 * 1000;

export const masteryLabels = ["Bekannt", "Abrufbar", "Anwendbar", "Pruefungsstabil", "Unter Zeitdruck stabil"];

export function todayKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function dateAfter(days: number, from = new Date()): string {
  return new Date(from.getTime() + Math.max(1, days) * dayMs).toISOString().slice(0, 10);
}

function isYesterday(date?: string): boolean {
  if (!date) return false;
  return date === todayKey(new Date(Date.now() - dayMs));
}

function daysBetween(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / dayMs);
}

export function createInitialState(): LearningState {
  return {
    version: 1,
    cards: {},
    sessions: [],
    importJobs: [],
    streak: 0,
    recoveryDays: 2,
    abilities: {},
  };
}

export function cardFor(state: LearningState, exerciseId: string): CardProgress {
  return (
    state.cards[exerciseId] ?? {
      interval: 0,
      due: todayKey(),
      correct: 0,
      wrong: 0,
      lapses: 0,
      mastery: 1,
    }
  );
}

export function isDue(state: LearningState, exercise: LearningExercise): boolean {
  return cardFor(state, exercise.id).due <= todayKey();
}

export function sameAnswerSet(selected: number[], correctAnswers: number[]): boolean {
  if (selected.length !== correctAnswers.length) return false;
  const normalized = [...selected].sort((a, b) => a - b);
  const expected = [...correctAnswers].sort((a, b) => a - b);
  return normalized.every((value, index) => value === expected[index]);
}

// Sortieraufgaben: korrekt nur, wenn die Sequenz exakt der richtigen Reihenfolge entspricht.
export function evaluateOrdering(correctOrder: string[], sequence: string[]): boolean {
  return correctOrder.length === sequence.length && correctOrder.every((v, i) => v === sequence[i]);
}

function modeWeight(mode: ModeId): number {
  return {
    daily: 1,
    case: 1.25,
    claim: 1.35,
    exam: 1.5,
  }[mode];
}

export function goodLearningSeconds(mode: ModeId, elapsedSeconds: number, correct: boolean): number {
  const bounded = Math.max(8, Math.min(elapsedSeconds, mode === "exam" ? 900 : 180));
  const correctnessWeight = correct ? 1 : 0.75;
  return Math.round(bounded * modeWeight(mode) * correctnessWeight);
}

export function scoreExercise(state: LearningState, exercise: LearningExercise, mode: ModeId): number {
  const card = cardFor(state, exercise.id);
  const attempts = card.correct + card.wrong;
  const dueBoost = isDue(state, exercise) ? 120 : 0;
  const weaknessBoost = card.wrong * 28 + card.lapses * 36;
  const noveltyBoost = attempts === 0 ? 45 : 0;
  const levelBoost = mode === "exam" ? exercise.levelNumber * 8 : 0;
  const modeBoost =
    mode === "claim" && exercise.skillTags.includes("claim-analysis")
      ? 42
      : mode === "case" && exercise.levelNumber >= 2
        ? 32
        : 0;

  // Difficulty-Proximity nur fuer statisch getaggte Items: bevorzugt Fragen nahe am Niveau.
  // Variantenfaehige Items haben keinen festen difficulty-Tag (sie adaptieren zur Anzeigezeit).
  const proximity = exercise.difficulty
    ? -Math.abs(DIFFICULTY_ORDER[exercise.difficulty] - abilityFor(state.abilities, exercise.skillTags)) * 10
    : 0;

  return dueBoost + weaknessBoost + noveltyBoost + levelBoost + modeBoost - card.correct * 7 + proximity;
}

export function filterForMode(
  exercises: LearningExercise[],
  mode: ModeId,
  pathId: LearningPathId | "all",
  state?: LearningState
): LearningExercise[] {
  return exercises.filter((exercise) => {
    if (pathId !== "all" && exercise.pathId !== pathId) return false;
    if (mode === "claim") return exercise.skillTags.includes("claim-analysis") || exercise.pathId === "epu-eqe";
    if (mode === "case") return exercise.levelNumber >= 2 || exercise.kind !== "single";
    // Daily Drill = Spaced Repetition: nur faellige oder neue Karten. Exam = alles (Simulation).
    if (mode === "daily" && state) {
      const card = state.cards[exercise.id];
      const attempts = card ? card.correct + card.wrong : 0;
      return attempts === 0 || isDue(state, exercise);
    }
    return true;
  });
}

// Interleaving (Spec §5.3): nach Score sortieren, dann Themen round-robin mischen,
// damit nicht mehrere gleiche Topics hintereinander kommen (Birnbaum 2013).
// Deterministisch (kein Zufall): Gruppenreihenfolge = erstes Auftreten im Score-Sort.
function interleaveByTopic(items: LearningExercise[]): LearningExercise[] {
  const groups = new Map<string, LearningExercise[]>();
  for (const item of items) {
    const list = groups.get(item.topic) ?? [];
    list.push(item);
    groups.set(item.topic, list);
  }
  const queues = [...groups.values()];
  const result: LearningExercise[] = [];
  let progressed = true;
  while (progressed) {
    progressed = false;
    for (const queue of queues) {
      const next = queue.shift();
      if (next) {
        result.push(next);
        progressed = true;
      }
    }
  }
  return result;
}

export function selectQueue(
  state: LearningState,
  exercises: LearningExercise[],
  mode: ModeId,
  pathId: LearningPathId | "all",
  limit = 10
): LearningExercise[] {
  const filtered = filterForMode(exercises, mode, pathId, state);
  const sorted = [...filtered].sort((a, b) => scoreExercise(state, b, mode) - scoreExercise(state, a, mode));
  return interleaveByTopic(sorted.slice(0, limit));
}

// Confidence-basiertes Intervall (Spec §5.2). good/again reproduzieren exakt den
// alten Binaerpfad (correct: prev*2+level, wrong: 1); hard/easy modulieren dazwischen.
// 45-Tage-Cap bleibt.
export function nextInterval(previousInterval: number, grade: Grade, levelNumber: number): number {
  const base = Math.max(1, previousInterval || 1);
  const raw =
    grade === "again"
      ? 1
      : grade === "hard"
        ? Math.max(1, Math.round(base * 1.2))
        : grade === "good"
          ? base * 2 + levelNumber
          : Math.round(base * 2.6) + levelNumber + 2;
  return Math.min(45, raw);
}

export function nextMastery(previousMastery: number, grade: Grade, mode: ModeId): number {
  if (grade === "again") return Math.max(1, previousMastery - 1);
  const gain = grade === "hard" ? 0.25 : grade === "easy" ? 0.75 : mode === "exam" ? 1 : 0.5;
  return Math.min(5, previousMastery + gain);
}

export function recordAttempt(
  state: LearningState,
  exercise: LearningExercise,
  mode: ModeId,
  selected: number[],
  elapsedSeconds: number,
  grade?: Grade,
  difficulty?: Difficulty,
  today: string = todayKey()
): { state: LearningState; event: SessionEvent; correct: boolean } {
  const graded = grade != null;
  const correct = graded ? grade !== "again" : sameAnswerSet(selected, exercise.answers);
  const effectiveGrade: Grade = graded ? grade : correct ? "good" : "again";
  const previous = cardFor(state, exercise.id);
  const itemLevel = DIFFICULTY_ORDER[difficulty ?? "medium"];
  const attemptScore = gradeToScore(effectiveGrade);
  const nextAbilities = { ...state.abilities };
  for (const skill of exercise.skillTags) {
    nextAbilities[skill] = updateAbility(state.abilities[skill] ?? DEFAULT_ABILITY, itemLevel, attemptScore);
  }
  const goodSeconds = goodLearningSeconds(mode, elapsedSeconds, correct);
  const nextCards = { ...state.cards };
  // Weiche Streak (Spec §6): ein Tagesabstand innerhalb des Recovery-Puffers haelt die
  // Streak statt sie auf 1 zuruckzusetzen - kein Verlust-Drama.
  const wasNewDay = state.lastPracticeDate !== today;
  const gap = state.lastPracticeDate ? daysBetween(state.lastPracticeDate, today) : 0;
  const withinRecovery = gap > 1 && gap - 1 <= state.recoveryDays;
  const nextStreak = !wasNewDay ? state.streak : gap === 1 || withinRecovery ? state.streak + 1 : 1;

  const nextCard: CardProgress = {
    ...previous,
    correct: previous.correct + (correct ? 1 : 0),
    wrong: previous.wrong + (correct ? 0 : 1),
    lapses: previous.lapses + (correct ? 0 : previous.correct > 0 ? 1 : 0),
    interval: nextInterval(previous.interval, effectiveGrade, exercise.levelNumber),
    mastery: nextMastery(previous.mastery, effectiveGrade, mode),
    last: todayKey(),
    streak: correct ? (previous.streak ?? 0) + 1 : 0,
  };

  nextCard.due = dateAfter(nextCard.interval);
  nextCards[exercise.id] = nextCard;

  const event: SessionEvent = {
    id: `${Date.now()}-${exercise.id}`,
    date: todayKey(),
    exerciseId: exercise.id,
    mode,
    pathId: exercise.pathId,
    topic: exercise.topic,
    skillTags: exercise.skillTags,
    correct,
    elapsedSeconds,
    goodLearningSeconds: goodSeconds,
    errorCategory: exercise.errorCategory,
  };

  return {
    state: {
      ...state,
      cards: nextCards,
      sessions: [...state.sessions, event].slice(-1000),
      streak: nextStreak,
      lastPracticeDate: today,
      abilities: nextAbilities,
    },
    event,
    correct,
  };
}

export function analyticsSummary(state: LearningState, exercises: LearningExercise[]) {
  const totalAttempts = state.sessions.length;
  const correctAttempts = state.sessions.filter((session) => session.correct).length;
  const goodSeconds = state.sessions.reduce((sum, session) => sum + session.goodLearningSeconds, 0);
  const due = exercises.filter((exercise) => isDue(state, exercise)).length;
  const stable = Object.values(state.cards).filter((card) => card.mastery >= 4).length;
  const activeSkills = new Set(state.sessions.flatMap((session) => session.skillTags)).size;

  return {
    totalAttempts,
    accuracy: totalAttempts ? Math.round((correctAttempts / totalAttempts) * 100) : 0,
    goodLearningMinutes: Math.round(goodSeconds / 60),
    due,
    stable,
    activeSkills,
    streak: state.streak,
  };
}

// Tagesziel (Spec WS-3): ein klares, didaktisch begruendetes Tagesziel statt
// reinem Mengen-Grind = heute faellige Wiederholungen + ein neuer Lernblock,
// gedeckelt auf eine gesunde Sessionlaenge. Rein funktional, kein Date.now.
export function dailyGoal(input: { dueCount: number; newBlockSize: number; capMinutes: number }) {
  const targetItems = input.dueCount + input.newBlockSize;
  return { targetItems, capMinutes: input.capMinutes };
}

// Wie viele Aufgaben wurden heute schon beantwortet (fuer den Tagesziel-Fortschritt).
export function attemptsToday(state: LearningState): number {
  return state.sessions.filter((session) => session.date === todayKey()).length;
}

// Stille Milestones (Spec §6): dezenter Hinweis beim Ueberschreiten einer Schwelle,
// kein Konfetti/Sound. Wird in submitAttempt/App ausgewertet und als Toast gezeigt.
export function milestoneFor(prev: LearningState, next: LearningState, exercises: LearningExercise[]): string | null {
  const a = analyticsSummary(prev, exercises);
  const b = analyticsSummary(next, exercises);
  if (a.goodLearningMinutes < 12 && b.goodLearningMinutes >= 12) {
    return "Stilles Ziel erreicht: 12 Minuten Good Learning Time heute.";
  }
  if (b.stable > a.stable) return "Eine weitere Karte ist pruefungsstabil.";
  return null;
}

export function masteryByPath(state: LearningState, exercises: LearningExercise[]) {
  return exercises.reduce<Record<string, { total: number; mastery: number; due: number }>>((acc, exercise) => {
    const entry = acc[exercise.pathId] ?? { total: 0, mastery: 0, due: 0 };
    const card = cardFor(state, exercise.id);
    entry.total += 1;
    entry.mastery += card.mastery || 1;
    entry.due += isDue(state, exercise) ? 1 : 0;
    acc[exercise.pathId] = entry;
    return acc;
  }, {});
}

// Empfehlung fuer die naechste Lern-Aktion (Spec WS-3, „naechste Aktion"-Sog):
// der Pfad mit den meisten faelligen Karten; bei Gleichstand der mit der
// niedrigsten Mastery (dort ist am meisten zu lernen). Rein funktional.
export function recommendNext(
  state: LearningState,
  exercises: LearningExercise[],
  paths: { id: string; label: string }[]
): { pathId: string; label: string; due: number } | null {
  const mastery = masteryByPath(state, exercises);
  const candidates = paths
    .map((p) => ({ pathId: p.id, label: p.label, ...(mastery[p.id] ?? { total: 0, mastery: 0, due: 0 }) }))
    .filter((c) => c.total > 0);
  if (!candidates.length) return null;
  candidates.sort((a, b) => b.due - a.due || a.mastery / a.total - b.mastery / b.total);
  const top = candidates[0];
  return { pathId: top.pathId, label: top.label, due: top.due };
}

// Mastery-Narrativ (Spec WS-3): Verteilung der Karten pro Pfad ueber vier Stufen
// (neu -> lernend -> gefestigt -> stabil). Macht Fortschritt als Bewegung sichtbar,
// nicht nur als Zahl. Stabil = mastery >= 4 (= pruefungsstabil, vgl. analyticsSummary).
export type MasteryStageCounts = { neu: number; lernend: number; gefestigt: number; stabil: number; total: number };

export function masteryStages(
  state: LearningState,
  exercises: LearningExercise[],
  paths: { id: string }[]
): Record<string, MasteryStageCounts> {
  const acc: Record<string, MasteryStageCounts> = {};
  for (const p of paths) acc[p.id] = { neu: 0, lernend: 0, gefestigt: 0, stabil: 0, total: 0 };
  for (const exercise of exercises) {
    const bucket = acc[exercise.pathId];
    if (!bucket) continue;
    const card = cardFor(state, exercise.id);
    bucket.total += 1;
    const seen = card.correct + card.wrong > 0;
    if (!seen) bucket.neu += 1;
    else if (card.mastery >= 4) bucket.stabil += 1;
    else if (card.mastery >= 3) bucket.gefestigt += 1;
    else bucket.lernend += 1;
  }
  return acc;
}

export function weaknessList(state: LearningState, exercises: LearningExercise[]) {
  const byTopic = exercises.reduce<Record<string, { wrong: number; total: number; tag: string }>>((acc, exercise) => {
    const card = cardFor(state, exercise.id);
    const entry = acc[exercise.topic] ?? { wrong: 0, total: 0, tag: exercise.skillTags[0] ?? "grundlagen" };
    entry.wrong += card.wrong;
    entry.total += card.correct + card.wrong;
    acc[exercise.topic] = entry;
    return acc;
  }, {});

  return Object.entries(byTopic)
    .filter(([, value]) => value.total > 0)
    .sort((a, b) => b[1].wrong / b[1].total - a[1].wrong / a[1].total)
    .slice(0, 6)
    .map(([topic, value]) => ({ topic, ...value }));
}

export function dailyQuests(state: LearningState, exercises: LearningExercise[]) {
  const summary = analyticsSummary(state, exercises);
  return [
    {
      id: "daily-good-learning-time",
      label: "12 Minuten Good Learning Time",
      done: summary.goodLearningMinutes >= 12,
      progress: Math.min(100, Math.round((summary.goodLearningMinutes / 12) * 100)),
    },
    {
      id: "daily-review-queue",
      label: "6 faellige Wiederholungen klaeren",
      done: state.sessions.filter((session) => session.date === todayKey()).length >= 6,
      progress: Math.min(100, Math.round((state.sessions.filter((session) => session.date === todayKey()).length / 6) * 100)),
    },
    {
      id: "daily-weak-spot",
      label: "Eine Schwachstelle gezielt treffen",
      done: state.sessions.some((session) => session.date === todayKey() && !session.correct),
      progress: state.sessions.some((session) => session.date === todayKey() && !session.correct) ? 100 : 30,
    },
  ];
}

export function createImportJob(input: {
  fileName: string;
  pathId: LearningPathId;
  markdown: string;
  sourceManifest: string[];
}): ImportJob {
  const markdown = input.markdown.trim();
  const lines = markdown.split(/\r?\n/).filter(Boolean);
  const heading = lines.find((line) => line.startsWith("#"))?.replace(/^#+\s*/, "") || input.fileName.replace(/\.pdf$/i, "");
  const checks: string[] = [];

  if (!markdown) checks.push("Markdown fehlt: PDF muss erst durch material-extractor laufen.");
  if (!lines.some((line) => /^#{1,3}\s/.test(line))) checks.push("Ueberschriftenstruktur fehlt oder ist zu flach.");
  if (lines.some((line) => line.length > 900)) checks.push("Sehr langer Block gefunden: Normalisierung pruefen.");
  if (!input.sourceManifest.length) checks.push("Quellenmanifest fehlt.");

  return {
    id: `import-${Date.now()}`,
    createdAt: new Date().toISOString(),
    fileName: input.fileName,
    status: checks.length ? "uploaded" : "ready-for-question-generation",
    pathId: input.pathId,
    markdownTitle: heading,
    markdownPreview: markdown.slice(0, 900),
    sourceManifest: input.sourceManifest,
    checks,
    extractionLog: [
      "PDF darf nur im material-extractor-Agent verarbeitet werden.",
      "Downstream-Agenten nutzen ausschliesslich Markdown, Quellenmanifest und Review-Status.",
      checks.length ? "Automatische Checks brauchen Review." : "Automatische Checks bestanden.",
    ],
  };
}
